import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { environment } from '../../../../../my-app/src/environments/environment';
import { formatNumber } from '../../self-service-visualization/shared/number-format.utils';
import { isMandatoryControl } from '../../utils/mandatory-control.utils';

interface ComputationColumn {
  name: string;
  type?: string;
}

interface ComputationEntry {
  id: string;
  expression: string;
  alias: string;
  raw: string;
}

interface NumericRangeFilter {
  column: string;
  absoluteMin: number;
  absoluteMax: number;
  currentMin: number;
  currentMax: number;
}

@Component({
  selector: 'app-input-box',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './input-box.component.html',
  styleUrl: './input-box.component.scss',
})
export class InputBoxComponent {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() formControl!: FormControl;

  /** Emits the entered value on change so a host (e.g. the DVM dashboard) can
   *  re-post the input control's payload and refresh the target charts. */
  @Output() valueChange = new EventEmitter<string>();

  private _data: any = null;

  @Input() set data(value: any) {
    this._data = value || null;
    this.label = this.resolveString(value?.label, value?.title, this.label, 'Input');
    this.placeholder = this.resolveString(value?.placeholder, this.placeholder, 'Enter a value');
    // Seed AND re-sync the displayed value from `data.value` — a setter (not
    // ngOnInit) runs on every push, so a target input control's live-updated
    // value (from a driving control's recompute) shows up here too, not just
    // on first load.
    if (value?.value !== undefined && value?.value !== null) {
      this.inputValue = this.roundIncomingValue(value.value, value?.decimals);
    }
  }

  /** Round a PUSHED value (recompute result / initial default) to the
   *  Format popup's `decimals` before it ever reaches the editable field — a
   *  computed value like `0.6118790192803075` showed up verbatim otherwise,
   *  decimals or not. Only applies to values we're SETTING from outside;
   *  never touches what the user is actively typing. */
  private roundIncomingValue(raw: unknown, decimals: unknown): string {
    const num = Number(raw);
    const d = Number(decimals);
    if (!Number.isFinite(num) || !Number.isFinite(d)) return String(raw);
    return num.toFixed(Math.max(0, Math.min(6, Math.floor(d))));
  }

  get data(): any {
    return this._data;
  }

  inputValue = '';
  computeOpen = false;
  currentExpression = '';
  currentAlias = '';
  expressionError = '';
  errorMessage = '';
  successMessage = '';
  loading = false;
  resultPreview: unknown = null;
  computations: ComputationEntry[] = [];
  numericFilters: NumericRangeFilter[] = [];

  constructor(private readonly http: HttpClient) {}

  /** Format-popup / config `mandatory` → `*` after the label. Presentation only. */
  get isMandatory(): boolean {
    return isMandatoryControl(this.data);
  }

  get title(): string {
    return this.resolveString(this.data?.title, this.label, 'Input');
  }

  /** Read-only formatted preview of the current value (scale + decimals from
   *  the DVM builder's Format popup: numberFormat/decimals) — shown ALONGSIDE
   *  the editable text field, which stays raw so typing an exact value keeps
   *  working. Hidden when no scale/decimals is configured, or the value isn't
   *  numeric (this control also accepts free text). */
  get formattedValue(): string | null {
    const numberFormat = this.data?.numberFormat;
    if (!numberFormat || numberFormat === 'NONE') return null;
    const numeric = Number(this.inputValue);
    if (this.inputValue === '' || Number.isNaN(numeric)) return null;
    return formatNumber(numeric, numberFormat, this.data?.decimals);
  }

  // ── Label / border styling (all optional — unset keeps the stock look) ────

  /** Where the label sits relative to the field. */
  get labelPosition(): 'top' | 'left' | 'right' | 'bottom' {
    const position = this.data?.labelPosition;
    return position === 'left' || position === 'right' || position === 'bottom' ? position : 'top';
  }

  get labelStyles(): Record<string, string> {
    const styles: Record<string, string> = {};
    const size = Number(this.data?.labelFontSize);
    if (Number.isFinite(size) && size > 0) styles['font-size'] = `${size}px`;
    const color = this.resolveString(this.data?.labelColor);
    if (color) styles['color'] = color;
    return styles;
  }

  /** Border colour for the field. Focus keeps its own highlight colour. */
  get inputStyles(): Record<string, string> {
    const color = this.resolveString(this.data?.inputBorderColor);
    return color ? { '--input-box-border': color } : {};
  }

  // ── Increase / decrease (stepper) ─────────────────────────────────────────
  // `data.stepper` draws the two arrow buttons inside the box; `data.step` is
  // the amount per click (default 1); `data.min` / `data.max` (optional)
  // bound the value. The typed value stays free text - only the buttons and
  // the arrow keys step it.

  get hasStepper(): boolean {
    return this.data?.stepper === true;
  }

  /** Amount per step: a positive number, else 1. */
  get stepSize(): number {
    const step = this.toFiniteNumber(this.data?.step);
    return step !== null && step > 0 ? step : 1;
  }

  get minValue(): number | null {
    return this.toFiniteNumber(this.data?.min);
  }

  get maxValue(): number | null {
    return this.toFiniteNumber(this.data?.max);
  }

  /** The button is live while a further step stays inside the bounds. */
  canStep(direction: 1 | -1): boolean {
    const current = this.toFiniteNumber(this.inputValue);
    if (current === null) return true;
    const limit = direction > 0 ? this.maxValue : this.minValue;
    return limit === null || (direction > 0 ? current < limit : current > limit);
  }

  /** Step the value by `step` in `direction`, clamp it, and emit it as a
   *  typed change. An empty / non-numeric box starts at min when set, else
   *  at one step from zero (as a native number input does). */
  stepBy(direction: 1 | -1): void {
    if (!this.canStep(direction)) return;
    const current = this.toFiniteNumber(this.inputValue);
    const next = current === null
      ? (this.minValue ?? direction * this.stepSize)
      : current + direction * this.stepSize;
    this.inputValue = this.formatStepped(this.clampToBounds(next));
    this.valueChange.emit(this.inputValue);
  }

  /** Arrow keys step the value when the stepper is on. */
  onStepKey(event: Event, direction: 1 | -1): void {
    if (!this.hasStepper) return;
    event.preventDefault();
    this.stepBy(direction);
  }

  private clampToBounds(value: number): number {
    const min = this.minValue;
    const max = this.maxValue;
    let next = value;
    if (min !== null && next < min) next = min;
    if (max !== null && next > max) next = max;
    return next;
  }

  /** Text for a stepped value: the configured `decimals`, else just enough
   *  places for the step (0.25 → 2), without floating-point noise. */
  private formatStepped(value: number): string {
    const decimals = this.toFiniteNumber(this.data?.decimals);
    if (decimals !== null) return value.toFixed(Math.max(0, Math.min(6, Math.floor(decimals))));
    const stepText = String(this.stepSize);
    const places = stepText.includes('.') ? Math.min(6, stepText.split('.')[1].length) : 0;
    return String(Number(value.toFixed(places)));
  }

  private toFiniteNumber(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === '') return null;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  }

  get entityName(): string {
    return this.resolveString(this.data?.computationEntityName, this.data?.entityName, this.data?.datasetName, '');
  }

  get allColumns(): ComputationColumn[] {
    const columns = this.data?.computationColumns;
    return Array.isArray(columns) ? columns : [];
  }

  get numericColumns(): ComputationColumn[] {
    return this.allColumns.filter((column) => column.type === 'number');
  }

  get canCompute(): boolean {
    // `hideCompute` on the widget data suppresses the on-the-fly compute (bolt)
    // button — self-service tiles that shouldn't expose it set this flag.
    if (this.data?.hideCompute) return false;
    return !!this.entityName || this.allColumns.length > 0;
  }

  toggleCompute(event: MouseEvent): void {
    event.stopPropagation();
    this.computeOpen = !this.computeOpen;
    if (this.computeOpen) {
      this.errorMessage = '';
      this.successMessage = '';
    }
  }

  closeCompute(): void {
    this.computeOpen = false;
  }

  appendColumn(column: ComputationColumn): void {
    this.currentExpression = this.currentExpression
      ? `${this.currentExpression} + ${column.name}`
      : column.name;
    if (!this.currentAlias) {
      this.currentAlias = column.name;
    }
    this.expressionError = '';
  }

  addComputation(): void {
    const expression = this.currentExpression.trim();
    const alias = this.currentAlias.trim();
    if (!expression) {
      this.expressionError = 'Expression is required.';
      return;
    }

    this.expressionError = '';
    this.computations = [
      ...this.computations,
      {
        id: `comp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        expression,
        alias: alias || expression,
        raw: alias ? `${expression} as ${alias}` : expression,
      },
    ];
    this.currentExpression = '';
    this.currentAlias = '';
  }

  removeComputation(id: string): void {
    this.computations = this.computations.filter((entry) => entry.id !== id);
  }

  toggleFilter(column: ComputationColumn): void {
    if (this.hasFilter(column.name)) {
      this.numericFilters = this.numericFilters.filter((filter) => filter.column !== column.name);
      return;
    }

    const values = this.computationRows
      .map((row) => Number(row?.[column.name]))
      .filter((value) => Number.isFinite(value));
    const absoluteMin = values.length ? Math.min(...values) : 0;
    const absoluteMax = values.length ? Math.max(...values) : 100;
    this.numericFilters = [
      ...this.numericFilters,
      { column: column.name, absoluteMin, absoluteMax, currentMin: absoluteMin, currentMax: absoluteMax },
    ];
  }

  hasFilter(columnName: string): boolean {
    return this.numericFilters.some((filter) => filter.column === columnName);
  }

  updateFilterValue(filter: NumericRangeFilter, key: 'currentMin' | 'currentMax', event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    if (!Number.isFinite(value)) return;
    this.numericFilters = this.numericFilters.map((current) => {
      if (current.column !== filter.column) return current;
      const next = { ...current, [key]: value };
      if (next.currentMin > next.currentMax) {
        return current;
      }
      return next;
    });
  }

  removeFilter(columnName: string): void {
    this.numericFilters = this.numericFilters.filter((filter) => filter.column !== columnName);
  }

  clearAll(): void {
    this.computations = [];
    this.numericFilters = [];
    this.currentExpression = '';
    this.currentAlias = '';
    this.expressionError = '';
    this.errorMessage = '';
    this.successMessage = '';
    this.resultPreview = null;
  }

  apply(): void {
    if (!this.entityName) {
      this.errorMessage = 'No entity name found for this input chart.';
      return;
    }
    if (!this.computations.length && !this.numericFilters.length) {
      this.errorMessage = 'Add at least one computation or range filter.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.http.post<Record<string, unknown>>(`${String(environment.URL || '').replace(/\/$/, '')}/getData`, {
      entityName: this.entityName,
      attributesList: this.computations.map((entry) => entry.raw),
      filterList: this.numericFilters.map((filter) => ({
        column: filter.column,
        operator: 'BETWEEN',
        value: [filter.currentMin, filter.currentMax],
      })),
    }).subscribe({
      next: (result) => {
        this.loading = false;
        this.successMessage = 'Data fetched successfully.';
        this.resultPreview = result;
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = err?.error?.message || err?.message || 'Failed to fetch data.';
      },
    });
  }

  trackByComputation(_index: number, entry: ComputationEntry): string {
    return entry.id;
  }

  trackByFilter(_index: number, entry: NumericRangeFilter): string {
    return entry.column;
  }

  private get computationRows(): Array<Record<string, unknown>> {
    const rows = this.data?.computationRows;
    return Array.isArray(rows) ? rows : [];
  }

  private resolveString(...values: unknown[]): string {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  }
}
